import React, { useEffect, useRef, useState } from 'react';

import Checkbox from 'components/partials/checkbox/checkbox';

import type { ReportUser } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-report-data';

interface UserAccessChangePayload {
  userId: string | number;
  hasAccess: boolean;
}

interface ReportAssignmentUsersProps {
  reportId: string | number;
  reportUsers: ReportUser[] | undefined;
  onUserAccessChange: (
    reportId: string | number,
    userData: UserAccessChangePayload
  ) => void;
  isDisabled: boolean;
  isClosing: boolean;
}

const ReportAssignmentUsers = ({
  reportId,
  reportUsers,
  onUserAccessChange,
  isDisabled,
  isClosing,
}: ReportAssignmentUsersProps): React.JSX.Element | null => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (!contentRef.current) return undefined;

    if (isClosing) {
      setHeight(contentRef.current.scrollHeight);
      const timer = setTimeout(() => setHeight(0), 10);

      return () => clearTimeout(timer);
    } else if (reportUsers) {
      const timer = setTimeout(
        () => contentRef.current && setHeight(contentRef.current.scrollHeight),
        10
      );

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [reportUsers, isClosing]);

  useEffect(() => {
    if (reportUsers && contentRef.current && !isClosing) {
      setHeight(contentRef.current.scrollHeight);
    }

    return () => {
      setHeight(0);
    };
  }, []);

  if (!reportUsers && !isClosing) {
    return null;
  }

  return (
    <tr>
      <td colSpan={4} className="p-0 overflow-hidden">
        <div
          className="transition-all duration-300 ease-in-out"
          style={{
            maxHeight: `${height}px`,
            opacity: height > 0 ? 1 : 0,
          }}
        >
          <div ref={contentRef}>
            <table className="w-full">
              <tbody>
                {reportUsers &&
                  reportUsers.map((user, index) => {
                    const isLastUser = index === reportUsers.length - 1;
                    const borderClass = isLastUser
                      ? 'py-2 px-4'
                      : 'border-b border-navy-200 py-2 px-4';

                    return (
                      <tr
                        key={user.id}
                        className="transition-colors duration-200"
                      >
                        <td className={`w-[5%] ${borderClass}`}></td>
                        <td className={`w-[55%] ${borderClass}`}>
                          <div className="text-gray-700">
                            {`${user.firstName} ${user.lastName}`}
                          </div>
                          <div className="text-gray-600 text-sm">
                            {user.jobTitle}
                          </div>
                        </td>
                        <td className={`text-center w-[20%] ${borderClass}`}>
                          <Checkbox
                            labelFor={`user-access-${reportId}-${user.id}`}
                            label=""
                            checkboxValue={String(user.id)}
                            checked={!!user.hasAccess}
                            onChange={(userData: UserAccessChangePayload) =>
                              onUserAccessChange(reportId, userData)
                            }
                            disabled={isDisabled}
                            customStylesLabel="flex justify-center"
                            aria-label={`Report access for ${user.firstName} ${user.lastName}`}
                          />
                        </td>
                        <td className={`w-[20%] ${borderClass}`}></td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default React.memo(ReportAssignmentUsers);
